// ─────────────────────────────────────────────
// KLOTHIQ — Print Utilities
// ─────────────────────────────────────────────
// Provides zero-dependency, isolated document printing that
// strips application chrome (sidebar, navbar, action buttons)
// and prints only the designated element or html content.

export interface PrintOptions {
  documentTitle?: string;
  onBeforePrint?: () => void;
  onAfterPrint?: () => void;
  signal?: AbortSignal;
}

/**
 * Prints target HTML content in a clean hidden iframe so the main page UI is unaffected.
 */
export function printHtmlContent(htmlContent: string, options?: PrintOptions): void {
  if (options?.signal?.aborted) return;
  options?.onBeforePrint?.();

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');

  document.body.appendChild(iframe);

  let finished = false;
  const cleanup = () => {
    if (finished) return;
    finished = true;
    options?.signal?.removeEventListener('abort', cleanup);
    iframe.remove();
    options?.onAfterPrint?.();
  };
  options?.signal?.addEventListener('abort', cleanup, { once: true });
  if (options?.signal?.aborted) {
    cleanup();
    return;
  }

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    console.error('Failed to access print iframe document');
    cleanup();
    return;
  }

  doc.open();
  const documentStyles = Array.from(
    document.querySelectorAll('style, link[rel="stylesheet"]'),
  ).map((element) => element.outerHTML).join('\n');
  doc.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${options?.documentTitle || 'KLOTHIQ Document'}</title>
        ${documentStyles}
        <style>
          @page {
            size: auto;
            margin: 12mm;
          }
          *, *::before, *::after {
            box-sizing: border-box;
          }
          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            color: #0f172a;
            background-color: #ffffff;
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @media print {
            body {
              background: #ffffff !important;
            }
            .no-print {
              display: none !important;
            }
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
    </html>
  `);
  doc.close();

  // Keep logos and the source layout in the printed document, including cold image loads.
  const imagesReady = Array.from(doc.images).map((image) =>
    image.complete ? Promise.resolve() : new Promise<void>((resolve) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
    }),
  );
  const resourcesReady = Promise.all([...imagesReady, doc.fonts.ready]);
  void Promise.race([
    resourcesReady,
    new Promise((resolve) => setTimeout(resolve, 5000)),
  ]).then(() => {
    if (finished) return;
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (err) {
      console.error('Printing error:', err);
    } finally {
      setTimeout(cleanup, 500);
    }
  });
}

/**
 * Triggers standard browser window print with temporary CSS class targeting print container
 */
export function printElementById(elementId: string, title: string = 'KLOTHIQ Document'): void {
  const el = document.getElementById(elementId);
  if (!el) {
    console.error(`Element #${elementId} not found for printing`);
    window.print();
    return;
  }

  printHtmlContent(el.outerHTML, { documentTitle: title });
}
